import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, XCircle, Clock, Loader2, FileSpreadsheet, RefreshCcw } from 'lucide-react';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import type { ResponseEnvelope } from '@/types/api';
import { Endpoints } from '@/lib/api/endpoints';

interface ImportJob {
  id:           string;
  jobName:      string;
  fileName:     string;
  fileType:     string;
  fileSizeBytes: number;
  totalRows:    number;
  importedRows: number;
  updatedRows:  number;
  skippedRows:  number;
  errorRows:    number;
  status:       string;
  errorSummary: string[] | null;
  startedAt:    string | null;
  completedAt:  string | null;
  createdAt:    string;
}

function StatusBadge({ status }: { status: string }) {
  const cfg = {
    COMPLETED:  { icon: CheckCircle2, color: '#34d399', bg: 'rgba(52,211,153,0.10)', border: 'rgba(52,211,153,0.25)', label: 'Completed' },
    FAILED:     { icon: XCircle,       color: '#f87171', bg: 'rgba(239,68,68,0.10)',   border: 'rgba(239,68,68,0.28)',  label: 'Failed' },
    PROCESSING: { icon: Loader2,       color: '#60a5fa', bg: 'rgba(59,130,246,0.10)',  border: 'rgba(59,130,246,0.25)', label: 'Processing' },
    PENDING:    { icon: Clock,         color: '#fbbf24', bg: 'rgba(251,191,36,0.10)',  border: 'rgba(251,191,36,0.28)', label: 'Pending' },
  }[status] ?? { icon: Clock, color: '#9ca3af', bg: 'rgba(156,163,175,0.10)', border: 'rgba(156,163,175,0.25)', label: status };

  const Icon = cfg.icon;
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:5,
      background: cfg.bg, border: `1px solid ${cfg.border}`,
      borderRadius:7, padding:'3px 9px',
      fontSize:12, fontWeight:500, color: cfg.color,
    }}>
      <Icon size={11} className={status === 'PROCESSING' ? 'animate-spin' : ''} />
      {cfg.label}
    </span>
  );
}

function formatBytes(b: number): string {
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function relTime(iso: string): string {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60)   return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400)return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function ImportHistorySection() {
  const { data: jobs, isLoading, refetch } = useQuery<ImportJob[]>({
    queryKey: ['connect-import-jobs'],
    queryFn:  async () => unwrap(await api.get<ResponseEnvelope<ImportJob[]>>(Endpoints.me.connect.importJobs)),
    refetchInterval: (q) => {
      const d = q.state.data;
      return Array.isArray(d) && d.some(j => j.status === 'PROCESSING') ? 3000 : false;
    },
  });

  if (isLoading) return (
    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'24px 0', color:'rgba(255,255,255,0.35)' }}>
      <Loader2 size={16} className="animate-spin" /> Loading import history…
    </div>
  );

  if (!jobs?.length) return (
    <div style={{
      display:'flex', flexDirection:'column', alignItems:'center',
      padding:'40px 0', gap:12, color:'rgba(255,255,255,0.30)',
    }}>
      <FileSpreadsheet size={32} style={{ opacity:0.3 }} />
      <p style={{ margin:0, fontSize:14 }}>No imports yet. Upload a CSV or XLSX file to get started.</p>
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <h3 style={{ margin:0, fontSize:15, fontWeight:600, color:'rgba(255,255,255,0.75)' }}>
          Import History
        </h3>
        <button
          onClick={() => refetch()}
          style={{
            display:'flex', alignItems:'center', gap:5,
            background:'none', border:'none', cursor:'pointer',
            color:'rgba(255,255,255,0.35)', fontSize:12,
            padding:'4px 8px', borderRadius:6,
          }}
        >
          <RefreshCcw size={11} /> Refresh
        </button>
      </div>

      <div style={{
        border:'1px solid rgba(96,165,250,0.12)',
        borderRadius:12, overflow:'hidden',
      }}>
        {/* Table header */}
        <div style={{
          display:'grid',
          gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 1fr',
          background:'rgba(255,255,255,0.03)',
          borderBottom:'1px solid rgba(96,165,250,0.10)',
          padding:'9px 16px',
          fontSize:11.5, fontWeight:600,
          color:'rgba(255,255,255,0.40)',
          letterSpacing:'0.05em', textTransform:'uppercase',
        }}>
          <span>File</span>
          <span>Status</span>
          <span>Imported</span>
          <span>Updated</span>
          <span>Skipped</span>
          <span>Date</span>
        </div>

        {jobs.map((job, i) => (
          <div
            key={job.id}
            style={{
              display:'grid',
              gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 1fr',
              alignItems:'center',
              padding:'12px 16px',
              borderBottom: i < jobs.length - 1 ? '1px solid rgba(96,165,250,0.07)' : 'none',
              background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)',
            }}
          >
            {/* File info */}
            <div>
              <div style={{ fontSize:13.5, color:'rgba(255,255,255,0.80)', fontWeight:500, marginBottom:2 }}>
                {job.fileName}
              </div>
              <div style={{ fontSize:11.5, color:'rgba(255,255,255,0.30)' }}>
                {formatBytes(job.fileSizeBytes)} · {job.totalRows.toLocaleString()} rows
              </div>
            </div>

            <StatusBadge status={job.status} />

            <span style={{ fontSize:13, color: job.importedRows > 0 ? '#34d399' : 'rgba(255,255,255,0.40)' }}>
              {job.importedRows.toLocaleString()}
            </span>

            <span style={{ fontSize:13, color: job.updatedRows > 0 ? '#60a5fa' : 'rgba(255,255,255,0.40)' }}>
              {job.updatedRows.toLocaleString()}
            </span>

            <span style={{ fontSize:13, color: job.skippedRows > 0 ? '#fbbf24' : 'rgba(255,255,255,0.40)' }}>
              {job.skippedRows.toLocaleString()}
            </span>

            <span style={{ fontSize:12.5, color:'rgba(255,255,255,0.35)' }}>
              {relTime(job.createdAt)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
